import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export default function SearchableSelect({
  value,
  options,
  onChange,
  placeholder = "Select",
  disabled = false,
  containerStyle = null,
  fieldShellStyle = null,
  fieldShellOpenStyle = null,
  fieldShellDisabledStyle = null,
  inputStyle = null,
  chevronButtonStyle = null,
  chevronStyle = null,
  menuStyle = null,
  optionStyle = null,
  optionActiveStyle = null,
  emptyStateStyle = null,
  emptyActionLabel = null,
  onEmptyAction = null,
  emptyActionDisabled = false,
  emptyActionStyle = null,
  emptyActionDisabledStyle = null,
  emptyActionHint = null,
  emptyActionHintStyle = null,
}) {
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const safeOptions = useMemo(
    () =>
      Array.isArray(options)
        ? options
            .map((option) => ({
              id: String(option?.id ?? ""),
              label: String(option?.label ?? option?.name ?? ""),
              searchText: String(option?.searchText ?? option?.label ?? option?.name ?? option?.id ?? "").toLowerCase(),
            }))
            .filter((option) => option.id && option.label)
        : [],
    [options]
  );
  const selectedOption = safeOptions.find((option) => option.id === String(value || "")) || null;
  const [query, setQuery] = useState(selectedOption?.label || "");
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const normalizedQuery = query.trim().toLowerCase();
  const trimmedQuery = query.trim();

  useEffect(() => {
    if (!open) {
      setQuery(selectedOption?.label || "");
    }
  }, [open, selectedOption]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (
        !wrapperRef.current?.contains(event.target) &&
        !menuRef.current?.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return undefined;

    const updateMenuPosition = () => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;

      const gap = 8;
      const viewportPadding = 16;
      const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
      const spaceAbove = rect.top - viewportPadding;
      const openAbove = spaceBelow < 180 && spaceAbove > spaceBelow;
      const maxHeight = Math.max(
        180,
        Math.min(320, openAbove ? spaceAbove - gap : spaceBelow - gap)
      );

      setMenuPosition({
        left: rect.left,
        top: openAbove ? null : rect.bottom + gap,
        bottom: openAbove ? window.innerHeight - rect.top + gap : null,
        width: rect.width,
        maxHeight,
      });
    };

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open]);

  const visibleOptions = useMemo(() => {
    if (!normalizedQuery) return safeOptions;
    return safeOptions.filter((option) => option.searchText.includes(normalizedQuery));
  }, [normalizedQuery, safeOptions]);

  const canShowEmptyAction =
    visibleOptions.length === 0 &&
    Boolean(trimmedQuery) &&
    typeof onEmptyAction === "function";
  const resolvedEmptyActionLabel = canShowEmptyAction
    ? (typeof emptyActionLabel === "function" ? emptyActionLabel(trimmedQuery) : emptyActionLabel) || `Add "${trimmedQuery}"`
    : "";
  const resolvedEmptyActionHint = canShowEmptyAction
    ? typeof emptyActionHint === "function"
      ? emptyActionHint(trimmedQuery)
      : emptyActionHint
    : "";

  const syncValueFromQuery = () => {
    const trimmedQuery = String(query || "").trim();
    if (!trimmedQuery) {
      onChange?.("");
      setQuery("");
      return;
    }

    const exactMatch = safeOptions.find((option) => option.label.toLowerCase() === trimmedQuery.toLowerCase());
    if (exactMatch) {
      onChange?.(exactMatch.id);
      setQuery(exactMatch.label);
      return;
    }

    setQuery(selectedOption?.label || "");
  };

  const handleSelect = (nextOption) => {
    onChange?.(nextOption.id);
    setQuery(nextOption.label);
    setOpen(false);
    window.requestAnimationFrame(() => inputRef.current?.blur());
  };

  const handleEmptyAction = () => {
    if (!canShowEmptyAction || emptyActionDisabled) return;
    onEmptyAction?.(trimmedQuery);
    setOpen(false);
    window.requestAnimationFrame(() => inputRef.current?.blur());
  };

  return (
    <div
      ref={wrapperRef}
      style={{
        ...styles.container,
        ...(containerStyle || {}),
        ...(disabled ? styles.containerDisabled : {}),
      }}
    >
      <div
        style={{
          ...styles.fieldShell,
          ...(fieldShellStyle || {}),
          ...(open ? styles.fieldShellOpen : {}),
          ...(open ? fieldShellOpenStyle || {} : {}),
          ...(disabled ? styles.fieldShellDisabled : {}),
          ...(disabled ? fieldShellDisabledStyle || {} : {}),
        }}
      >
        <input
          ref={inputRef}
          type="text"
          value={query}
          onFocus={() => !disabled && setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            if (!disabled) setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setOpen(false);
              setQuery(selectedOption?.label || "");
              inputRef.current?.blur();
            }
            if (event.key === "Enter") {
              event.preventDefault();
              if (visibleOptions[0]) {
                handleSelect(visibleOptions[0]);
              } else if (canShowEmptyAction && !emptyActionDisabled) {
                handleEmptyAction();
              } else {
                syncValueFromQuery();
                setOpen(false);
              }
            }
          }}
          onBlur={() => {
            window.setTimeout(() => {
              if (!wrapperRef.current?.contains(document.activeElement)) {
                syncValueFromQuery();
                setOpen(false);
              }
            }, 0);
          }}
          placeholder={placeholder}
          disabled={disabled}
          style={{
            ...styles.input,
            ...(inputStyle || {}),
          }}
        />
        <button
          type="button"
          style={{
            ...styles.chevronButton,
            ...(chevronButtonStyle || {}),
          }}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            if (disabled) return;
            if (open) {
              syncValueFromQuery();
              setOpen(false);
              inputRef.current?.blur();
              return;
            }
            setOpen(true);
            inputRef.current?.focus();
          }}
          disabled={disabled}
          aria-label={`Toggle ${placeholder}`}
        >
          <span
            style={{
              ...styles.chevron,
              ...(chevronStyle || {}),
            }}
          >
            {open ? "^" : "v"}
          </span>
        </button>
      </div>

      {open && !disabled && menuPosition ? createPortal(
        <div
          ref={menuRef}
          style={{
            ...styles.menu,
            left: menuPosition.left,
            top: menuPosition.top,
            bottom: menuPosition.bottom,
            width: menuPosition.width,
            ...(menuStyle || {}),
          }}
        >
          <div style={{ ...styles.options, maxHeight: menuPosition.maxHeight }}>
            {visibleOptions.length === 0 ? (
              <div style={styles.emptyWrap}>
                <div
                  style={{
                    ...styles.emptyState,
                    ...(emptyStateStyle || {}),
                  }}
                >
                  No options found.
                </div>
                {canShowEmptyAction ? (
                  <button
                    type="button"
                    style={{
                      ...styles.emptyAction,
                      ...(emptyActionStyle || {}),
                      ...(emptyActionDisabled ? styles.emptyActionDisabled : {}),
                      ...(emptyActionDisabled ? emptyActionDisabledStyle || {} : {}),
                    }}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={handleEmptyAction}
                    disabled={emptyActionDisabled}
                  >
                    {resolvedEmptyActionLabel}
                  </button>
                ) : null}
                {canShowEmptyAction && resolvedEmptyActionHint ? (
                  <div
                    style={{
                      ...styles.emptyActionHint,
                      ...(emptyActionHintStyle || {}),
                    }}
                  >
                    {resolvedEmptyActionHint}
                  </div>
                ) : null}
              </div>
            ) : (
              visibleOptions.map((option) => {
                const active = option.id === String(value || "");
                return (
                  <button
                    key={option.id}
                    type="button"
                    style={{
                      ...styles.option,
                      ...(optionStyle || {}),
                      ...(active ? styles.optionActive : {}),
                      ...(active ? optionActiveStyle || {} : {}),
                    }}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleSelect(option)}
                  >
                    {option.label}
                  </button>
                );
              })
            )}
          </div>
        </div>,
        document.body
      ) : null}
    </div>
  );
}

const styles = {
  container: {
    position: "relative",
    width: "100%",
  },
  containerDisabled: {
    opacity: 0.72,
  },
  fieldShell: {
    width: "100%",
    borderRadius: 16,
    border: "1px solid rgba(132,42,45,0.16)",
    background: "#fffcfb",
    display: "flex",
    alignItems: "center",
    minHeight: 50,
    overflow: "hidden",
    boxSizing: "border-box",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.74)",
  },
  fieldShellOpen: {
    borderColor: "rgba(132,42,45,0.34)",
    boxShadow: "0 0 0 4px rgba(132,42,45,0.09)",
  },
  fieldShellDisabled: {
    background: "#f7f1ee",
  },
  input: {
    flex: 1,
    minWidth: 0,
    border: "none",
    outline: "none",
    background: "transparent",
    color: "#2f2833",
    fontSize: 14,
    fontWeight: 600,
    padding: "14px 16px",
    boxSizing: "border-box",
  },
  chevronButton: {
    border: "none",
    background: "transparent",
    color: "#6f6670",
    width: 46,
    height: 50,
    cursor: "pointer",
    flexShrink: 0,
  },
  chevron: {
    fontSize: 16,
    lineHeight: 1,
  },
  menu: {
    position: "fixed",
    zIndex: 10020,
    borderRadius: 20,
    border: "1px solid rgba(132,42,45,0.16)",
    background: "#fffdfb",
    boxShadow: "0 22px 44px rgba(76,44,51,0.12)",
    padding: 12,
    boxSizing: "border-box",
  },
  options: {
    maxHeight: 260,
    overflowY: "auto",
    display: "grid",
    gap: 8,
  },
  emptyWrap: {
    display: "grid",
    gap: 8,
  },
  option: {
    width: "100%",
    border: "1px solid rgba(132,42,45,0.08)",
    borderRadius: 14,
    background: "#fff",
    padding: "11px 12px",
    cursor: "pointer",
    textAlign: "left",
    color: "#2f2833",
    fontSize: 14,
    fontWeight: 600,
  },
  optionActive: {
    borderColor: "rgba(132,42,45,0.22)",
    background: "#fff5f1",
    color: "#842A2D",
    fontWeight: 600,
  },
  emptyState: {
    padding: "12px 10px",
    color: "#7f8795",
    fontSize: 13,
  },
  emptyAction: {
    width: "100%",
    border: "1px solid rgba(132,42,45,0.22)",
    borderRadius: 14,
    background: "#fff5f1",
    padding: "11px 12px",
    cursor: "pointer",
    textAlign: "left",
    color: "#842A2D",
    fontSize: 13,
    fontWeight: 700,
  },
  emptyActionDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  emptyActionHint: {
    padding: "0 10px 6px",
    color: "#7f8795",
    fontSize: 12,
    lineHeight: 1.45,
  },
};
